// src/components/myAGT/AvailabilityCalendar.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Grid,
  Paper,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Alert,
  FormHelperText
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import es from 'date-fns/locale/es';

const DAYS_OF_WEEK = [
  { id: 0, name: 'Domingo' },
  { id: 1, name: 'Lunes' },
  { id: 2, name: 'Martes' },
  { id: 3, name: 'Miércoles' },
  { id: 4, name: 'Jueves' },
  { id: 5, name: 'Viernes' },
  { id: 6, name: 'Sábado' }
];

const AvailabilityCalendar = ({ availability, onChange, isMobile }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [exceptionHours, setExceptionHours] = useState({
    start: '09:00',
    end: '18:00'
  });
  const [minBookingNotice, setMinBookingNotice] = useState(
    availability.minBookingNotice || 30
  );
  const [errorMessage, setErrorMessage] = useState('');

  // Manejar cambio en días de trabajo
  const handleWorkDayChange = (dayId) => {
    const newWorkDays = availability.workDays.includes(dayId)
      ? availability.workDays.filter(id => id !== dayId)
      : [...availability.workDays, dayId];
    
    onChange({
      ...availability,
      workDays: newWorkDays
    });
  };

  // Manejar cambio en horas de trabajo
  const handleWorkHoursChange = (e) => {
    const { name, value } = e.target;
    onChange({
      ...availability,
      workHours: {
        ...availability.workHours,
        [name]: value
      }
    });
  };

  // Manejar cambio en duración de citas
  const handleSlotDurationChange = (e) => {
    onChange({
      ...availability,
      slotDuration: parseInt(e.target.value)
    });
  };

  // Función para verificar si un día está en el horario regular
  const isDayInWorkDays = (date) => {
    if (!date) return false;
    // Convertir a día de la semana (0-6, donde 0 es domingo)
    const dayOfWeek = date.getDay();
    return availability.workDays.includes(dayOfWeek);
  };

  // Añadir excepción (fecha bloqueada o con horario especial)
  const handleAddException = () => {
    if (!selectedDate) return;
    
    // Verificar si el día está en el horario regular
    if (!isDayInWorkDays(selectedDate)) {
      setErrorMessage("No puedes configurar un horario especial para un día que no está en tu horario regular");
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    // Verificar si ya existe la excepción
    const existingIndex = availability.exceptions.findIndex(
      exc => exc.date === dateStr
    );
    
    let newExceptions;
    
    if (existingIndex >= 0) {
      // Actualizar excepción existente
      newExceptions = [...availability.exceptions];
      newExceptions[existingIndex] = {
        date: dateStr,
        isBlocked: false,
        hours: { ...exceptionHours }
      };
    } else {
      // Añadir nueva excepción
      newExceptions = [
        ...availability.exceptions,
        {
          date: dateStr,
          isBlocked: false,
          hours: { ...exceptionHours }
        }
      ];
    }
    
    onChange({
      ...availability,
      exceptions: newExceptions
    });
    
    // Limpiar selección
    setSelectedDate(null);
    setErrorMessage('');
  };

  // Bloquear fecha completa
  const handleBlockDate = () => {
    if (!selectedDate) return;
    
    // Verificar si el día está en el horario regular
    if (!isDayInWorkDays(selectedDate)) {
      setErrorMessage("No puedes bloquear un día que no está en tu horario regular");
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    // Verificar si ya existe la excepción
    const existingIndex = availability.exceptions.findIndex(
      exc => exc.date === dateStr
    );
    
    let newExceptions;
    
    if (existingIndex >= 0) {
      // Actualizar excepción existente
      newExceptions = [...availability.exceptions];
      newExceptions[existingIndex] = {
        date: dateStr,
        isBlocked: true
      };
    } else {
      // Añadir nueva excepción
      newExceptions = [
        ...availability.exceptions,
        {
          date: dateStr,
          isBlocked: true
        }
      ];
    }
    
    onChange({
      ...availability,
      exceptions: newExceptions
    });
    
    // Limpiar selección
    setSelectedDate(null);
    setErrorMessage('');
  };
  
  // Actualizar el objeto de disponibilidad cuando cambia el tiempo de anticipación
  useEffect(() => {
    onChange({
      ...availability,
      minBookingNotice: minBookingNotice
    });
  }, [minBookingNotice]);

  return (
    <Box sx={{ mt: 2 }}>
      {errorMessage && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Horario regular
          </Typography>
          
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Días de atención
            </Typography>
            
            <FormGroup>
              <Grid container spacing={1}>
                {DAYS_OF_WEEK.map((day) => (
                  <Grid item xs={6} key={day.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={availability.workDays.includes(day.id)}
                          onChange={() => handleWorkDayChange(day.id)}
                          size="small"
                        />
                      }
                      label={day.name}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </Paper>
          
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Horario de atención
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hora inicio"
                  type="time"
                  name="start"
                  value={availability.workHours.start}
                  onChange={handleWorkHoursChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hora fin"
                  type="time"
                  name="end"
                  value={availability.workHours.end}
                  onChange={handleWorkHoursChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>
          
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Duración de citas
            </Typography>
            
            <FormControl fullWidth size="small">
              <InputLabel id="slot-duration-label">Duración</InputLabel>
              <Select
                labelId="slot-duration-label"
                value={availability.slotDuration}
                onChange={handleSlotDurationChange}
                label="Duración"
              >
                <MenuItem value={15}>15 minutos</MenuItem>
                <MenuItem value={30}>30 minutos</MenuItem>
                <MenuItem value={45}>45 minutos</MenuItem>
                <MenuItem value={60}>1 hora</MenuItem>
                <MenuItem value={90}>1 hora 30 minutos</MenuItem>
                <MenuItem value={120}>2 horas</MenuItem>
              </Select>
            </FormControl>
          </Paper>
          
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Tiempo mínimo de anticipación
            </Typography>
            
            <FormControl fullWidth size="small">
              <InputLabel id="min-booking-notice-label">Anticipación</InputLabel>
              <Select
                labelId="min-booking-notice-label"
                value={minBookingNotice}
                onChange={(e) => setMinBookingNotice(e.target.value)}
                label="Anticipación"
              >
                <MenuItem value={30}>30 minutos</MenuItem>
                <MenuItem value={60}>1 hora</MenuItem>
                <MenuItem value={120}>2 horas</MenuItem>
                <MenuItem value={360}>6 horas</MenuItem>
                <MenuItem value={720}>12 horas</MenuItem>
                <MenuItem value={1440}>1 día</MenuItem>
                <MenuItem value={2880}>2 días</MenuItem>
                <MenuItem value={4320}>3 días</MenuItem>
              </Select>
              <FormHelperText>
                Tiempo mínimo con el que un paciente puede agendar una cita
              </FormHelperText>
            </FormControl>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Excepciones
          </Typography>
          
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Fechas especiales
            </Typography>
            
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Seleccionar fecha"
                value={selectedDate}
                onChange={setSelectedDate}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                disablePast
              />
            </LocalizationProvider>
            
            {selectedDate && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Opciones para {selectedDate.toLocaleDateString()}:
                </Typography>
                
                {!isDayInWorkDays(selectedDate) && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Este día no está incluido en tu horario regular. Para configurarlo, primero añádelo en los días de atención.
                  </Alert>
                )}
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Hora inicio"
                      type="time"
                      value={exceptionHours.start}
                      onChange={(e) => setExceptionHours({ ...exceptionHours, start: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      size="small"
                      disabled={!isDayInWorkDays(selectedDate)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Hora fin"
                      type="time"
                      value={exceptionHours.end}
                      onChange={(e) => setExceptionHours({ ...exceptionHours, end: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      size="small"
                      disabled={!isDayInWorkDays(selectedDate)}
                    />
                  </Grid>
                </Grid>
                
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={handleAddException}
                    disabled={!isDayInWorkDays(selectedDate)}
                  >
                    Guardar horario especial
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    size="small"
                    onClick={handleBlockDate}
                    disabled={!isDayInWorkDays(selectedDate)}
                  >
                    Bloquear día
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
          
          {availability.exceptions.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Fechas configuradas
              </Typography>
              
              {availability.exceptions.map((exception, index) => (
                <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {new Date(exception.date).toLocaleDateString()}
                    {exception.isBlocked ? 
                      ' - Bloqueado' : 
                      ` - Horario: ${exception.hours.start} a ${exception.hours.end}`
                    }
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AvailabilityCalendar;